import React, { useEffect, useState, useMemo } from 'react'; 
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';

// --- THERMOMETER COMPONENT ---
const DonationProgress = ({ total, goal }) => {
    const progress = goal > 0 ? Math.min(100, (total / goal) * 100) : 0;
    const totalDollars = (total / 100).toFixed(2);
    const goalDollars = (goal / 100).toFixed(2);

    return (
        <div className="donation-progress-container" style={{ marginTop: '0rem', marginBottom: '1rem' }}>
            <h3 style={{ marginTop: '1.25rem', marginBottom: '0.75rem' }}>Fundraising Progress</h3>
            <div className="thermometer-bar-wrapper">
                <div 
                    className="thermometer-bar-fill" 
                    style={{ width: `${progress}%` }}
                >
                </div>
            </div>
            <p className="progress-text">
                ${totalDollars} raised toward the goal of ${goalDollars} 
            </p>
        </div>
    );
};
// --- END THERMOMETER COMPONENT ---

const gradeOptions = [
  'Kindergarten', '1st Grade', '2nd Grade',
  '3rd Grade', '4th Grade', '5th Grade'
];

const stripePromise = loadStripe('pk_live_51UIIlBEfWrShKqXxNrjs92336NyxepQt0upmiobZmyRmFCKtNo6zxkSTBYSKKiEEZmSY4j0xEk8LmpmLEHIdQ89T00GZTEtWax');

// --- CHECKOUT FORM ---
const CheckoutForm = ({ clientSecret, onDonationSuccess, onDonationError, onCancel, onProcessing }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPaymentMessage('');

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    if (onProcessing) onProcessing();

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setPaymentMessage(submitError.message);
      setLoading(false);
      onDonationError(submitError.message);
      return;
    }

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: window.location.href,
      },
    });

    if (confirmError) {
      if (confirmError.type === "card_error" || confirmError.type === "validation_error") {
        setPaymentMessage(confirmError.message);
      } else {
        setPaymentMessage("An unexpected error occurred.");
      }
      setLoading(false);
      onDonationError(confirmError.message);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {loading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px'
        }}>
          <p style={{ fontWeight: 'bold', color: '#0050a0', fontSize: '1.1rem' }}>
            Processing donation, please wait...
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
        <PaymentElement />
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', justifyContent: 'center', marginTop: '1rem' }}>
          <button 
            type="button" 
            onClick={onCancel} 
            disabled={loading}
            style={{ 
              backgroundColor: '#dc3545', 
              color: '#fff', 
              border: 'none', 
              padding: '12px 20px', 
              borderRadius: '5px', 
              cursor: loading ? 'not-allowed' : 'pointer' 
            }}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            disabled={!stripe || loading} 
            style={{ 
              flexGrow: 1, 
              backgroundColor: loading ? '#888' : '#0050a0', 
              color: '#fff', 
              border: 'none', 
              padding: '12px 20px', 
              borderRadius: '5px', 
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            {loading ? 'Processing...' : 'Complete Donation'}
          </button>
        </div>
        {paymentMessage && <div style={{ color: 'red', marginTop: '10px' }}>{paymentMessage}</div>}
      </form>
    </div>
  );
};

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
};

const FakeDonationForm = () => {
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);

  const [searchMode, setSearchMode] = useState('name');
  const [grade, setGrade] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedStudentName, setSelectedStudentName] = useState('');
  
  const [donorName, setDonorName] = useState('');
  const [email, setEmail] = useState('');
  
  const [amount, setAmount] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  
  // Custom message state tracking error status explicitly
  const [message, setMessage] = useState(null);
  const [isErrorMessage, setIsErrorMessage] = useState(false);

  const [searchName, setSearchName] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [donationFinalized, setDonationFinalized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [totalDonated, setTotalDonated] = useState(0);
  const [fundraisingGoal, setFundraisingGoal] = useState(0);

  const [coverFee, setCoverFee] = useState(false);

  // --- LAWN SIGN STATES & TOGGLE ---
  const [lawnSignEnabled, setLawnSignEnabled] = useState(true);
  const [showLawnSignModal, setShowLawnSignModal] = useState(false);
  const [hasLawnSign, setHasLawnSign] = useState(null);
  const [lawnSignMessage, setLawnSignMessage] = useState('');

  const setAppMessage = (msg, isError = false) => {
    setMessage(msg);
    setIsErrorMessage(isError);
  };

  useEffect(() => {
    if ((clientSecret && !donationFinalized) || showLawnSignModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [clientSecret, donationFinalized, showLawnSignModal]);

  const cleanStringForSearch = (str) => {
    return str.replace(/[^a-zA-Z]/g, '');
  };

  const effectiveDonationAmount = useMemo(() => {
    const base = parseFloat(amount) || 0;
    return hasLawnSign ? base + 20 : base;
  }, [amount, hasLawnSign]);

  const { donationAmount, processingFee, totalCharge } = useMemo(() => {
    if (effectiveDonationAmount === 0) {
      return { donationAmount: 0, processingFee: '0.00', totalCharge: 0 };
    }
    
    const calculatedTotal = (effectiveDonationAmount + 0.30) / (1 - 0.03); 
    const fee = calculatedTotal - effectiveDonationAmount;
    
    return {
      donationAmount: effectiveDonationAmount,
      processingFee: fee.toFixed(2),
      totalCharge: calculatedTotal,
    };
  }, [effectiveDonationAmount]);

  useEffect(() => {
    fetch('https://server-ojqx.onrender.com/api/teachers')
      .then(res => res.json())
      .then(setTeachers)
      .catch(() => setAppMessage('Failed to load teachers.', true));

    fetch('https://server-ojqx.onrender.com/api/students')
      .then(res => res.json())
      .then(setAllStudents)
      .catch(() => setAppMessage('Failed to load students.', true));

    fetch('https://server-ojqx.onrender.com/api/settings')
      .then(res => res.json())
      .then(data => {
        if (typeof data.lawnSignEnabled === 'boolean') {
          setLawnSignEnabled(data.lawnSignEnabled);
        }
      })
      .catch(() => console.error('Failed to fetch settings'));
  }, []);

  useEffect(() => {
    fetch('https://server-ojqx.onrender.com/api/donations/progress')
        .then(res => res.json())
        .then(data => {
            setTotalDonated(data.totalDonated);
            setFundraisingGoal(data.goal);
        })
        .catch(err => console.error('Error fetching donation progress:', err));
  }, [donationFinalized]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const paymentIntentId = query.get('payment_intent');
    const redirectStatus = query.get('redirect_status');

    if (paymentIntentId && redirectStatus === 'succeeded' && !donationFinalized) {
      setIsProcessing(true);
      const finalizeDonation = async () => {
        try {
          const res = await fetch('https://server-ojqx.onrender.com/api/donations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ paymentIntentId })
          });
          const data = await res.json();
          if (res.ok) {
            setAppMessage('Thank you for your donation! It has been successfully processed!', false);
            setDonationFinalized(true);
            setClientSecret('');
            window.history.replaceState({}, document.title, window.location.pathname);
          } else {
            setAppMessage(data.error, true);
          }
        } catch (err) {
          setAppMessage(err.message, true);
        } finally {
          setIsProcessing(false);
        }
      };
      finalizeDonation();
    }
  }, [donationFinalized]);

  useEffect(() => {
    if (selectedTeacher) {
      fetch(`https://server-ojqx.onrender.com/api/teachers/${selectedTeacher}/students`)
        .then(res => res.json())
        .then(setStudents)
        .catch(() => setAppMessage('Failed to load students.', true));
    } else {
      setStudents([]);
    }
  }, [selectedTeacher]);

  useEffect(() => {
    if (searchMode === 'name' && searchName) {
      const lowerCaseCleanSearch = cleanStringForSearch(searchName).toLowerCase();
      const searchWords = lowerCaseCleanSearch.split(' ');
      setFilteredStudents(
        allStudents.filter(s => {
          const lowerCaseCleanStudentName = cleanStringForSearch(s.name).toLowerCase();
          if (lowerCaseCleanStudentName.includes(lowerCaseCleanSearch)) return true;
          const studentNameWords = s.name.split(' ');
          return searchWords.every(searchWord => {
            return studentNameWords.some(studentWord => 
              cleanStringForSearch(studentWord).toLowerCase().startsWith(searchWord)
            );
          });
        })
      );
    } else {
      setFilteredStudents([]);
    }
  }, [searchName, allStudents, searchMode]);

  const handleSearchSelect = (student) => {
    const studentTeacher = teachers.find(t => t._id === student.teacher);
    if (!studentTeacher) {
      setAppMessage('Teacher not found for this student.', true);
      return;
    }
    setGrade(studentTeacher.grade);
    setSelectedTeacher(studentTeacher._id);
    setSelectedStudent(student._id);
    setSearchName('');
    setSelectedStudentName(student.name);
    setFilteredStudents([]);
    setAppMessage(null);
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();
    const baseAmount = parseFloat(amount) || 0;
    if (!selectedTeacher || !selectedStudent || !baseAmount || baseAmount <= 0) {
      setAppMessage('Please select a student and enter a valid donation amount.', true);
      return;
    }
    if (!email || !validateEmail(email)) {
      setAppMessage('Please enter a valid email address for the receipt.', true);
      return;
    }

    setAppMessage(null);

    if (!lawnSignEnabled) {
      executePaymentIntentCreation(false, '');
      return;
    }

    setHasLawnSign(null);
    setLawnSignMessage('');
    setShowLawnSignModal(true);
  };

  const executePaymentIntentCreation = async (signChosen, signMsg) => {
    if (signChosen && signMsg.trim().length > 100) {
      alert("Lawn sign message must be 100 characters or less.");
      return;
    }

    setShowLawnSignModal(false);
    setIsProcessing(true);
    setAppMessage(null);

    const activeDonationAmount = signChosen ? (parseFloat(amount) || 0) + 20 : (parseFloat(amount) || 0);
    const calculatedTotalCharge = (activeDonationAmount + 0.30) / (1 - 0.03);
    const stripeAmount = coverFee ? calculatedTotalCharge : activeDonationAmount;

    const payload = { 
      stripeAmount: stripeAmount,
      donationAmount: activeDonationAmount,
      teacherId: selectedTeacher, 
      studentId: selectedStudent, 
      email,
      donorName,
      hasLawnSign: !!signChosen,
      lawnSignMessage: signChosen ? signMsg : ''
    };

    try {
      const res = await fetch('https://server-ojqx.onrender.com/api/payments/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      
      if (!res.ok) {
        setAppMessage(data.error, true);
        setIsProcessing(false);
        return;
      }
      
      const { clientSecret: newClientSecret } = data;
      setClientSecret(newClientSecret);
      setIsProcessing(false);
    } catch (err) {
      setAppMessage(`Failed to create payment intent: ${err.message}`, true);
      setIsProcessing(false);
    }
  };

  const handlePresetClick = (val) => {
    setAmount(val);
    setSelectedPreset(val);
  };

  const handleResetForm = () => {
    setDonationFinalized(false);
    setClientSecret('');
    setIsProcessing(false);
    setAppMessage(null);
    setAmount('');
    setSelectedPreset(null);
    setDonorName('');
    setEmail('');
    setSelectedTeacher('');
    setSelectedStudent('');
    setSelectedStudentName('');
    setGrade('');
    setSearchName('');
    setFilteredStudents([]);
    setCoverFee(false);
    setShowLawnSignModal(false);
    setHasLawnSign(null);
    setLawnSignMessage('');
  };

  const handleCancelPayment = () => {
    const confirmCancel = window.confirm("Are you sure you want to cancel the donation process?");
    if (confirmCancel) {
      handleResetForm();
    }
  };

  const filteredTeachers = grade ? teachers.filter(t => t.grade === grade) : [];
  const appearance = { theme: 'stripe' };
  const options = { clientSecret, appearance };

  const handleAmountChange = (e) => {
    setAmount(e.target.value);
    setSelectedPreset(null);
  };

  return (
    <>
      <h2>Make a Donation</h2>

      {/* --- STANDARD FORM (Visible when no payment is finalized) --- */}
      {!donationFinalized && (
        <>
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={() => {
                setSearchMode('name');
                setGrade('');
                setSelectedTeacher('');
                setSelectedStudent('');
                setSearchName('');
                setFilteredStudents([]);
                setSelectedStudentName('');
                setAppMessage(null);
              }}
              style={{
                backgroundColor: searchMode === 'name' ? '#003f7f' : '#f0f0f0',
                color: searchMode === 'name' ? '#fff' : '#000',
                padding: '8px 15px',
                borderRadius: '5px',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Search by Student Name
            </button>
            <button
              type="button"
              onClick={() => {
                setSearchMode('teacher');
                setGrade('');
                setSelectedTeacher('');
                setSelectedStudent('');
                setSearchName('');
                setFilteredStudents([]);
                setSelectedStudentName('');
                setAppMessage(null);
              }}
              style={{
                backgroundColor: searchMode === 'teacher' ? '#003f7f' : '#f0f0f0',
                color: searchMode === 'teacher' ? '#fff' : '#000',
                padding: '8px 15px',
                borderRadius: '5px',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Find Student by Teacher
            </button>
          </div>

          {searchMode === 'name' && (
            <div style={{ position: 'relative', marginBottom: '1rem' }}>
              <input
                type="text"
                className="form-control"
                value={searchName}
                onChange={e => {
                    setSearchName(e.target.value);
                    setSelectedStudentName('');
                }}
                placeholder="Start typing student name"
                style={{ marginBottom: '0'}}
              />
              {filteredStudents.length > 0 && searchName && (
                <ul style={{
                  position: 'absolute', top: '100%', left: '0', right: '0', backgroundColor: 'white',
                  border: '1px solid #ccc', listStyle: 'none', padding: '0', margin: '0',
                  maxHeight: '200px', overflowY: 'auto', zIndex: 100
                }}>
                  {filteredStudents.map(student => (
                    <li
                      key={student._id}
                      onClick={() => handleSearchSelect(student)}
                      style={{ padding: '0.5rem 1rem', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                    >
                      {student.name} ({teachers.find(t => t._id === student.teacher)?.name})
                    </li>
                  ))}
                </ul>
              )}
              {searchName && filteredStudents.length === 0 && (
                  <div style={{ marginTop: '0.5rem', color: '#888' }}>No students found matching "{searchName}"</div>
              )}
              {selectedStudentName && (
                <div style={{ marginTop: '0.75rem', color: '#003f7f', fontWeight: 'bold' }}>
                  Making a donation on behalf of: {selectedStudentName}
                </div>
              )}
            </div>
          )}

          {searchMode === 'teacher' && (
            <>
              <select className="form-control select" value={grade} required onChange={e => { setGrade(e.target.value); setSelectedTeacher(''); setSelectedStudent(''); }}>
                <option value="">Select Grade Level</option>
                {gradeOptions.map((g, idx) => (<option key={idx} value={g}>{g}</option>))}
              </select>
              <select className="form-control select" value={selectedTeacher} disabled={!grade} required onChange={e => { setSelectedTeacher(e.target.value); setSelectedStudent(''); }}>
                <option value="">Select a Teacher</option>
                {filteredTeachers.map(t => (<option key={t._id} value={t._id}>{t.name}</option>))}
              </select>
              <select className="form-control select" value={selectedStudent} disabled={!selectedTeacher} required onChange={e => {
                setSelectedStudent(e.target.value);
                const sObj = students.find(s => s._id === e.target.value);
                if (sObj) setSelectedStudentName(sObj.name);
              }}>
                <option value="">Select Your Name</option>
                {students.map(s => (<option key={s._id} value={s._id}>{s.name}</option>))}
              </select>
            </>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', margin: '1rem 0' }}>
            {[50, 100, 250, 500].map(val => (
              <button type="button" key={val} onClick={() => handlePresetClick(val)} style={{ backgroundColor: selectedPreset === val ? '#003f7f' : '#fff', color: selectedPreset === val ? '#fff' : '#000', border: '1px solid #ccc', padding: '0.6rem 1rem', borderRadius: '4px', cursor: 'pointer', flexGrow: 1 }}>
                ${val}
              </button>
            ))}
            <input type="number" placeholder="Amount (USD)" value={amount} onChange={handleAmountChange} required min="1" step="0.01" style={{ flexGrow: 1, padding: '0.6rem', minWidth: '120px' }} />
          </div>

          <input type="text" className="form-control" placeholder="(Optional) Donation made by:" value={donorName} onChange={e => setDonorName(e.target.value)} />
          <input type="email" className="form-control" placeholder="Email for receipt" value={email} onChange={e => setEmail(e.target.value)} required />

          {donationAmount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '5px', backgroundColor: '#f9f9f9', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
              <input type="checkbox" id="cover-fee" checked={coverFee} onChange={e => setCoverFee(e.target.checked)} style={{ width: 'auto', height: 'auto' }} />
              <label htmlFor="cover-fee" style={{ margin: 0, fontSize: '0.9rem', color: '#333' }}>
                {coverFee ? (<strong>You're helping cover the fees! Your new total is ${totalCharge.toFixed(2)}.</strong>) : (<span>Yes, I'd like to add <strong>${processingFee}</strong> to cover the processing fees.</span>)}
              </label>
            </div>
          )}

          {/* --- STUDENT NOTIFICATION NOTICE --- */}
          <p style={{ 
            fontSize: '1.05rem', 
            fontWeight: 'bold', 
            color: '#000000', 
            marginTop: '0rem', 
            marginBottom: '1.0rem', 
            textAlign: 'center' 
          }}>
            The student will be notified via email of this donation.
          </p>

          <button onClick={handleProceedToPayment} disabled={isProcessing} style={{ padding: '12px', fontSize: '1.1rem', width: '100%' }}>
            {isProcessing ? 'Setting up Checkout...' : 'Proceed to Payment'}
          </button>
        </>
      )}

      {/* --- LAWN SIGN UPSELL MODAL --- */}
      {showLawnSignModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.82)',
          zIndex: 999999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '1.75rem',
            width: '100%',
            maxWidth: '480px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            boxSizing: 'border-box'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#003f7f', textAlign: 'center', fontSize: '1.35rem' }}>
              {hasLawnSign === true ? 'Personalize Your Lawn Sign' : 'Add a Personalized Lawn Sign!'}
            </h3>

            {hasLawnSign === null && (
              <>
                <p style={{ fontSize: '1.05rem', color: '#333', lineHeight: '1.4', textAlign: 'center', marginBottom: '1rem' }}>
                  Would you like to add an additional <strong>$20</strong> to your donation to have an 11" x 17" lawn sign with a customizable message to your student displayed during the event?
                </p>

                {/* --- DEADLINE NOTICE --- */}
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeeba',
                  color: '#856404',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  marginBottom: '1.25rem'
                }}>
                  ⏰ Note: The option to add a lawn sign ends on October 30 at 5:00 PM.
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setHasLawnSign(false)}
                    style={{ padding: '12px 20px', borderRadius: '6px', border: '1px solid #999', backgroundColor: '#e0e0e0', color: '#333333', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', flex: 1 }}
                  >
                    No, thanks
                  </button>
                  <button
                    type="button"
                    onClick={() => setHasLawnSign(true)}
                    style={{ padding: '12px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#28a745', color: '#fff', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', flex: 1 }}
                  >
                    Yes, add $20!
                  </button>
                </div>
              </>
            )}

            {hasLawnSign === true && (
              <div style={{ marginTop: '0.5rem' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.25rem', color: '#333' }}>
                  Enter your personalized message:
                </label>
                <span style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                  (100 characters max)
                </span>
                <textarea
                  value={lawnSignMessage}
                  onChange={(e) => setLawnSignMessage(e.target.value.slice(0, 100))}
                  placeholder={`e.g. Go ${selectedStudentName || 'Student'}! So proud of you!`}
                  rows={3}
                  maxLength={100}
                  style={{ width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #ccc', fontSize: '1rem', boxSizing: 'border-box' }}
                />
                <div style={{ textAlign: 'right', fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  {lawnSignMessage.length} / 100 characters
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                  <button
                    type="button"
                    onClick={() => setHasLawnSign(null)}
                    style={{ padding: '10px 16px', borderRadius: '6px', border: '1px solid #999', backgroundColor: '#e0e0e0', color: '#333333', fontWeight: 'bold', cursor: 'pointer' }}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => executePaymentIntentCreation(true, lawnSignMessage)}
                    style={{ flex: 1, padding: '12px', borderRadius: '6px', border: 'none', backgroundColor: '#003f7f', color: '#fff', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer' }}
                  >
                    Proceed to Payment (${coverFee ? totalCharge.toFixed(2) : donationAmount.toFixed(2)})
                  </button>
                </div>
              </div>
            )}

            {hasLawnSign === false && (
              <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
                <p style={{ margin: '0 0 1rem 0', color: '#555' }}>No problem! Proceeding with your donation.</p>
                <button
                  type="button"
                  onClick={() => executePaymentIntentCreation(false, '')}
                  style={{ padding: '12px', width: '100%', borderRadius: '6px', border: 'none', backgroundColor: '#003f7f', color: '#fff', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer' }}
                >
                  Proceed to Payment (${coverFee ? totalCharge.toFixed(2) : donationAmount.toFixed(2)})
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- FULLSCREEN TAKEOVER: Blocks entire page, top navbar, & fixes mobile scroll --- */}
      {clientSecret && !donationFinalized && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.82)',
          zIndex: 999999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: '1rem',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '1.5rem',
            width: '100%',
            maxWidth: '520px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            boxSizing: 'border-box',
            marginTop: 'auto',
            marginBottom: 'auto'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '1.25rem', borderBottom: '1px solid #eee', paddingBottom: '1rem' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#0050a0', fontSize: '1.4rem' }}>Complete Your Donation</h3>
              <p style={{ margin: 0, fontSize: '1.05rem', color: '#333' }}>
                Supporting: <strong>{selectedStudentName}</strong>
              </p>
              {hasLawnSign && (
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.95rem', color: '#28a745', fontWeight: 'bold' }}>
                  Includes Personalized Lawn Sign (+$20.00)
                </p>
              )}
              <p style={{ margin: '0.4rem 0 0 0', fontSize: '1.3rem', fontWeight: 'bold', color: '#283F94' }}>
                Total Charge: ${coverFee ? totalCharge.toFixed(2) : donationAmount.toFixed(2)}
              </p>
            </div>

            <Elements stripe={stripePromise} options={options}>
              <CheckoutForm
                clientSecret={clientSecret}
                onDonationSuccess={() => {
                    setAppMessage('Thank you for your donation! It has been successfully processed.', false);
                    setIsProcessing(false);
                    setDonationFinalized(true);
                    setClientSecret('');
                }}
                onDonationError={(err) => {
                    setAppMessage(err, true);
                    setIsProcessing(false);
                }}
                onCancel={handleCancelPayment}
                onProcessing={() => {
                    setIsProcessing(true);
                }}
              />
            </Elements>
          </div>
        </div>
      )}

      {/* --- SUCCESS STATE & "MAKE ANOTHER DONATION" BUTTON --- */}
      {donationFinalized && (
        <div style={{
          backgroundColor: '#f6fff8',
          border: '2px solid #28a745',
          borderRadius: '10px',
          padding: '2rem',
          marginTop: '1.5rem',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#28a745', margin: '0 0 0.5rem 0' }}>Thank You!</h3>
          <p style={{ fontSize: '1.15rem', color: '#333', margin: '0 0 1.5rem 0' }}>
            {message || 'Your donation has been successfully processed.'}
          </p>
          <button
            type="button"
            onClick={handleResetForm}
            style={{
              backgroundColor: '#003f7f',
              color: '#ffffff',
              padding: '12px 28px',
              fontSize: '1.1rem',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Make Another Donation
          </button>
        </div>
      )}

      {/* Primary Status / Error Message (when not finalized) */}
      {message && !donationFinalized && (
        <div className={isErrorMessage ? 'error-message' : 'success-message'} style={{ marginTop: '1rem', fontWeight: 'bold', fontSize: '1.1rem' }}>
          {message}
        </div>
      )}

      <p style={{ fontSize: '0.9rem', color: '#555', marginTop: '1.25rem', marginBottom: '0.5rem', textAlign: 'center' }}>
        If you have any questions or need help please email: manorhavenschoolpta@gmail.com
      </p>

      <DonationProgress total={totalDonated} goal={fundraisingGoal} />
    </>
  );
};

export default FakeDonationForm;
